<script setup lang="ts">
/********************
 * PROPS & EMITS     *
 ********************/
export interface CiFavoriteTileProps {
  favorite: any;
  index: number;
  timeoutActive?: boolean;
}

const props = withDefaults(defineProps<CiFavoriteTileProps>(), {
  timeoutActive: false,
});

const emit = defineEmits<{
  'ci-favorite-item-remove-favorite': [value: { slug: string; index: number }];
  'ci-favorite-item-undo-remove-favorite': [value: { slug: string }];
}>();

/********************
 * COMPOSITIONS      *
 ********************/
const localePath = useLocalePath();
const {
  comparePrice,
  decimalSeparator,
  favoriteIcon,
  markForRemoval,
  markedForRemoval,
  onFavoriteClicked,
  priceDecimals,
  priceNumberBeforeComma,
  setHoverState,
  undoIcon,
  undoMarkForRemoval,
} = useFavoriteItem({
  favorite: props.favorite,
  index: props.index,
  timeoutActive: props.timeoutActive,
});
const { $gettext, $pgettext } = useGettext();
const { fixImageUrlAndCheckWebp } = useWebp();

/********************
 * REFS & VARS       *
 ********************/
const altTextForFavoriteIcon = computed(() => {
  return $pgettext('favorite item', 'Remove favorite');
});

/********************
 * FUNCTIONS         *
 ********************/
function getImage(webp: boolean = false): any {
  return fixImageUrlAndCheckWebp(props.favorite.image, webp).url;
}

function onMarkForRemoval() {
  emit('ci-favorite-item-remove-favorite', { slug: props.favorite.slug, index: props.index });
  markForRemoval();
}

function onUndoMarkForRemoval() {
  emit('ci-favorite-item-undo-remove-favorite', { slug: props.favorite.slug });
  undoMarkForRemoval();
}
</script>

<template>
  <div
    v-if="!markedForRemoval && !props.timeoutActive"
    class="relative"
  >
    <CiAwesomeIcon
      :alt="altTextForFavoriteIcon"
      :icon="favoriteIcon"
      class="icon absolute right-4 top-4 z-10 cursor-pointer fill-danger"
      data-testid="ci-favorite-tile--icon"
      ratio="0.9"
      @click="onMarkForRemoval()"
      @mouseleave="setHoverState(false)"
      @mouseover="setHoverState(true)"
    />
    <nuxt-link
      :to="localePath({ name: RBN_CAMPSITE_DETAIL, params: { identifier: props.favorite.slug } })"
      class="block hover:no-underline"
      :class="{ 'pointer-events-none': !props.favorite.is_active }"
      data-testid="ci-favorite-tile"
      @click="onFavoriteClicked(props.index + 1)"
    >
      <div class="h-[17.5rem] rounded shadow-ci">
        <div
          class="relative h-48 overflow-hidden rounded bg-gray-40 bg-center bg-no-repeat"
          :class="{ 'bg-gray-10': props.favorite.image, 'opacity-30': !props.favorite.is_active }"
        >
          <picture>
            <source
              :srcset="getImage(true)"
              type="image/webp"
            />
            <source
              :srcset="getImage"
              type="image/jpg"
            />
            <img
              class="absolute left-0 top-0 size-full object-cover"
              :src="getImage"
              loading="lazy"
              alt=" "
            />
            <!-- CP name + image -->
          </picture>
          <div
            class="row relative h-full after:absolute after:left-0 after:top-0 after:block after:size-full after:bg-gradient-to-b after:from-transparent after:from-50% after:to-black after:content-['']"
          >
            <div class="col-12 flex flex-col">
              <div class="z-20 mt-auto p-4">
                <div class="text-sm font-medium text-primary">
                  {{ props.favorite.city }}
                </div>
                <div
                  class="m-0 font-medium text-white"
                  :data-testid="`ci-favorite-tile--slug-${props.favorite.slug}`"
                >
                  {{ props.favorite.name }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="relative p-4">
          <template v-if="props.favorite.is_active">
            <CiRatingRadial
              :rating="favorite.rating_avg_overall"
              class="chart-wrapper absolute top-1/2 size-[3.8rem] -translate-y-1/2 p-0"
              font-class="font-bold"
            />
            <div class="ml-[1.9rem] flex min-h-14 justify-end rounded bg-gray-20 py-1 pr-2 text-black">
              <div
                v-if="comparePrice"
                class="flex items-center"
              >
                <div class="mr-2 self-end font-medium leading-8">
                  {{ $gettext('From') }}
                </div>
                <div class="text-[2.5rem] font-normal">
                  <span>{{ priceNumberBeforeComma }}</span><span>{{ decimalSeparator }}</span>
                </div>
                <div class="mt-1 self-start font-medium">
                  {{ priceDecimals }}
                </div>
                <div class="h1 mb-0 ml-1 text-[2.5rem] font-normal">
                  €
                </div>
              </div>
              <div
                v-else
                class="mb-0 mr-4 self-center text-[2.5rem]"
              >
                -
              </div>
            </div>
          </template>
          <div
            v-else
            class="flex h-14 w-full"
            :class="{ 'items-center': favorite.is_active }"
          >
            <span class="mb-0 mr-4 py-1 font-bold text-black">{{ $gettext('Campsite not available') }}</span>
          </div>
        </div>
      </div>
    </nuxt-link>
  </div>
  <div
    v-else
    class="favorite-undo"
  >
    <div class="rounded border border-dark bg-white shadow-ci md:hidden">
      <div class="col-12 flex items-center justify-between p-1">
        <div class="my-2 justify-start">
          {{ $gettext('Campsite removed!') }}
        </div>
        <CiButton
          class="button"
          :icon="undoIcon"
          icon-ratio="0.9"
          icon-before
          @click="onUndoMarkForRemoval()"
        >
          <span>{{ $gettext('Undo') }}</span>
        </CiButton>
      </div>
    </div>
    <div class="hidden rounded border border-dark bg-white shadow-ci md:block">
      <div class="h-[17.4rem]">
        <div class="flex h-48 flex-col justify-center pt-4">
          <div class="text-center">
            {{ $gettext('Campsite removed from favorites!') }}
          </div>
        </div>
        <div class="flex flex-col justify-center">
          <div class="m-auto">
            <CiButton
              class="button button--primary px-4"
              :icon="undoIcon"
              icon-ratio="0.7"
              icon-before
              @click="onUndoMarkForRemoval()"
            >
              <span>{{ $gettext('Undo action') }}</span>
            </CiButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style></style>
